import React from "react";
import { Badge, type BadgeProps } from "@shared/ui/badge";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@shared/lib/utils";

export interface BadgeListProps {
  items?: string[] | string | null;
  count?: number;
  maxCount?: number;
  variant?: BadgeProps["variant"];
  className?: string;
  badgeClassName?: string;
  moreBadgeClassName?: string;
  fallback?: React.ReactNode;
}

export const BadgeList: React.FC<BadgeListProps> = ({
  items,
  count,
  maxCount,
  variant = "outline",
  className,
  badgeClassName,
  moreBadgeClassName,
  fallback = <span className="text-gray-500 text-sm">NA</span>,
}) => {
  const visibleLimit = count ?? maxCount ?? 2;

  let list: string[] = [];
  if (Array.isArray(items)) {
    list = items.filter(Boolean);
  } else if (typeof items === "string" && items.trim()) {
    list = items
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  if (list.length === 0) {
    return <>{fallback}</>;
  }

  const visibleItems = list.slice(0, visibleLimit);
  const remainingItems = list.slice(visibleLimit);

  if (remainingItems.length === 0) {
    return (
      <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
        {visibleItems.map((item, index) => (
          <Badge
            key={index}
            variant={variant}
            className={cn(
              "text-[11px] font-medium px-2 py-0.5 rounded-full",
              badgeClassName
            )}
          >
            {item}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {visibleItems.map((item, index) => (
        <Badge
          key={index}
          variant={variant}
          className={cn(
            "text-[11px] font-medium px-2 py-0.5 rounded-full",
            badgeClassName
          )}
        >
          {item}
        </Badge>
      ))}

      <TooltipPrimitive.Provider delayDuration={50}>
        <TooltipPrimitive.Root>
          <TooltipPrimitive.Trigger asChild>
            <button
              type="button"
              className={cn(
                "inline-flex items-center rounded-full border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700 text-[11px] font-medium px-1.5 py-0.5 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-gray-300 shrink-0",
                moreBadgeClassName
              )}
            >
              +{remainingItems.length} more
            </button>
          </TooltipPrimitive.Trigger>

          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              side="top"
              align="center"
              sideOffset={5}
              className="z-[99999] max-w-xs rounded-xl border border-gray-200 bg-white p-2.5 shadow-xl animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
            >
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                {list.map((item, idx) => (
                  <Badge
                    key={idx}
                    variant={variant}
                    className={cn(
                      "text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border-gray-200",
                      badgeClassName
                    )}
                  >
                    {item}
                  </Badge>
                ))}
              </div>
              <TooltipPrimitive.Arrow className="fill-white drop-shadow-xs" />
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </div>
  );
};

export default BadgeList;
